import React, { useState } from 'react';
import { 
  Typography, 
  Paper, 
  Box, 
  Accordion, 
  AccordionSummary, 
  AccordionDetails,
  TextareaAutosize,
  IconButton,
  Grid,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  MenuItem,
  Select,
  InputLabel
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PageBackground from '../components/PageBackground';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { add, sub, format } from 'date-fns';

const EasyAccess: React.FC = () => {
  // State to track which text areas are in edit mode
  const [editableStates, setEditableStates] = useState({
    commitment: false,
    decline: false,
    preApproval: false,
    within120Days: false,
    cbAlert: false,
    bfsConfirmableNotes: false,
    matchFileInfo: false,
  });

  // State to store the content of each text area
  const [textContent, setTextContent] = useState({
    commitment: `Hi ,
Thank you for choosing First National Excalibur.
Attached is the commitment for this deal. Daryl will be helping us for fulfillment—please send all supporting documentation to:
Daryl.strom@firstnational.ca
Please also ensure that the appraisal is addressed to First National Financial LP and is received directly from the appraiser upon completion.
Kindly be reminded Cindy will review existing documents and provide an updated condition list.
Best regards,
Nik Athanasopoulos
Underwriter, Excalibur
First National Financial LP
647-800-2167
nik.athanasopoulos@firstnational.ca
1140 W Pender Street, Suite 600 Vancouver, BC V6E 4G1`,

    decline: `If requested by broker:
Hi ,
Thank you for the submission.
As per your request, we will be sending this file back.
I hope we can assist you on the next one.
Kind regards,
Nik Athanasopoulos
Underwriter, Excalibur
First National Financial LP
647-800-2167
nik.athanasopoulos@firstnational.ca
1140 W Pender Street, Suite 600 Vancouver, BC V6E 4G1

If we are declining:
Hi ,
Thank you for the submission.
As per our communication, we will be sending this file back.
I hope we can assist you on the next one.
Kind regards,
Nik Athanasopoulos
Underwriter, Excalibur
First National Financial LP
647-800-2167
nik.athanasopoulos@firstnational.ca
1140 W Pender Street, Suite 600 Vancouver, BC V6E 4G1`,

    preApproval: `Hi ,
Thank you for choosing First National Excalibur.
Attached is the commitment for this pre-approval.
Please feel welcomed to submit the live deal when ready and we will be happy to help.
Kind regards,
Nik Athanasopoulos
Underwriter, Excalibur
First National Financial LP
647-800-2167
nik.athanasopoulos@firstnational.ca
1140 W Pender Street, Suite 600 Vancouver, BC V6E 4G1`,

    within120Days: `Hi ,
I hope you are all doing well.
I am following up on subject file because given that we have a financing date of and we are within 120 days of possession.
Please confirm whether the client is still proceeding, and if so, is the above possession date correct? If not, please advise to push closing.
Please let me know if you have any questions.
Have a wonderful day.
Best Regards,`,

    cbAlert: `CONSUMER ALERT VERIFIED.
Details:
Spoke with:
Date:
Phone number called:
Client confirmed their details as follows:
Date of Birth -
Current employer -
Current address -
The address of the subject property -
The broker acting on the application -
All details match application.`,

    bfsConfirmableNotes: `Version 1
**Business notes
Confirmation of ownership:
Business operating as:
Confirmed date of operation:
Registered Owner:
Income:
2022 NOA line 150 - $
2023 NOA line 150 - $
Please note since the NOAs encompass all sources of income we have retained the T1 General on file to qualify the income derived from client's business:
2022 T1 Gen Line 12000 - $ *T5 on file confirming Dividends from.
2023 T1 Gen Line 12000 - $ *T5 on file confirming Dividends from c.
2Y average: $
Variance:
CRA Amount owing paid in full - Confirmation under docs log.

Version 2
**Business notes
Confirmation of ownership:
Business operating as:
Confirmed date of operation:
Registered Owner:
Income:
2022 NOA line 150 - $
2023 NOA line 150 - $
Please note since the NOAs encompass all sources of income we have retained the T1 General on file to qualify the income derived from client's business:
2022 T1 Gen Line 13500 - $
2023 T1 Gen Line 13500 - $
2Y average: $
Variance:
CRA Amount owing paid in full - Confirmation under docs log.`,

    matchFileInfo: `Hi James,

Please provide match info below:

Borrowers' name = 
FN# (S230) = 
Property (S220) = 
P+I (S250) = 
Balance (S251) = 
NSFs (S254) = 
Notes (S261) = 

Thank you in advance,`,
  });

  // Date calculator states
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [timeValue, setTimeValue] = useState<number>(0);
  const [timeUnit, setTimeUnit] = useState<string>('days');
  const [operation, setOperation] = useState<string>('add');
  const [resultDate, setResultDate] = useState<Date | null>(null);

  // Toggle edit mode for a specific section
  const toggleEditMode = (section: keyof typeof editableStates) => {
    setEditableStates(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Handle text change in the textarea
  const handleTextChange = (section: keyof typeof textContent, newText: string) => {
    setTextContent(prev => ({
      ...prev,
      [section]: newText
    }));
  };

  const calculateDate = () => {
    if (!startDate || timeValue === 0) return;
    
    const dateOperation = operation === 'add' ? add : sub;
    
    let duration: any = {};
    duration[timeUnit] = timeValue;
    
    const result = dateOperation(startDate, duration);
    setResultDate(result);
  };

  // Create an accordion section
  const createAccordionSection = (
    title: string,
    section: keyof typeof editableStates,
    expanded: boolean = false
  ) => {
    return (
      <Accordion 
        defaultExpanded={expanded} 
        sx={{ mb: 0.5, backgroundColor: 'rgba(240, 245, 250, 0.9)' }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`${section}-content`}
          id={`${section}-header`}
          sx={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.03)', 
            minHeight: '42px', 
            '& .MuiAccordionSummary-content': { 
              margin: '8px 0' 
            } 
          }}
        >
          <Typography variant="h6">{title}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ position: 'relative' }}>
            <TextareaAutosize
              value={textContent[section]}
              onChange={(e) => handleTextChange(section, e.target.value)}
              disabled={!editableStates[section]}
              style={{
                width: '100%',
                minHeight: '200px',
                padding: '12px',
                fontFamily: 'inherit',
                fontSize: '0.875rem',
                borderColor: editableStates[section] ? '#1976d2' : '#e0e0e0',
                resize: 'vertical',
                backgroundColor: editableStates[section] ? '#fff' : '#f5f5f5',
              }}
            />
            <Box sx={{ 
              position: 'absolute', 
              bottom: '10px', 
              right: '10px',
              display: 'flex',
              gap: '8px'
            }}>
              <IconButton 
                size="small" 
                onClick={() => toggleEditMode(section)}
                color={editableStates[section] ? "primary" : "default"}
              >
                {editableStates[section] ? <SaveIcon /> : <EditIcon />}
              </IconButton>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <PageBackground>
      <Typography variant="h4" gutterBottom sx={{ color: '#333', fontWeight: 600, textShadow: 'none' }}>
        Templates
      </Typography>
      
      <Box sx={{ mt: 2 }}>
        <Paper sx={{ 
          p: 3, 
          backgroundColor: 'rgba(255, 255, 255, 0.92)', 
          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.06)',
          borderRadius: 2,
          border: '1px solid rgba(240, 240, 240, 0.9)',
        }}>
          {createAccordionSection('Commitment', 'commitment', true)}
          {createAccordionSection('Decline', 'decline')}
          {createAccordionSection('Pre-approval', 'preApproval')}
          {createAccordionSection('Within 120Days', 'within120Days')}
          {createAccordionSection('CB Alert', 'cbAlert')}
          {createAccordionSection('BFS Confirmable Notes', 'bfsConfirmableNotes')}
          {createAccordionSection('Match File Info', 'matchFileInfo')}
        </Paper>

        {/* Date Calculator Tool */}
        <Paper sx={{ 
          mt: 4,
          p: 3, 
          backgroundColor: 'rgba(255, 255, 255, 0.92)', 
          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.06)',
          borderRadius: 2,
          border: '1px solid rgba(240, 240, 240, 0.9)',
        }}>
          <Typography variant="h5" gutterBottom sx={{ color: '#333', fontWeight: 600 }}>
            Date Calculator for Mortgage Underwriting
          </Typography>
          
          <Box sx={{ mt: 3 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(newDate: Date | null) => setStartDate(newDate)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <TextField 
                    label="Value"
                    type="number"
                    value={timeValue}
                    onChange={(e) => setTimeValue(Number(e.target.value))}
                    InputProps={{ inputProps: { min: 0 } }}
                    fullWidth
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel id="time-unit-label">Time Unit</InputLabel>
                    <Select
                      labelId="time-unit-label"
                      value={timeUnit}
                      label="Time Unit"
                      onChange={(e) => setTimeUnit(e.target.value)}
                    >
                      <MenuItem value="days">Days</MenuItem>
                      <MenuItem value="weeks">Weeks</MenuItem>
                      <MenuItem value="months">Months</MenuItem>
                      <MenuItem value="years">Years</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Operation</FormLabel>
                    <RadioGroup
                      row
                      value={operation}
                      onChange={(e) => setOperation(e.target.value)}
                    >
                      <FormControlLabel value="add" control={<Radio />} label="Add" />
                      <FormControlLabel value="subtract" control={<Radio />} label="Subtract" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} display="flex" alignItems="flex-end">
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={calculateDate}
                    fullWidth
                    sx={{ height: '56px' }}
                  >
                    Calculate
                  </Button>
                </Grid>
                
                <Grid item xs={12}>
                  <Paper 
                    elevation={1} 
                    sx={{ 
                      p: 2, 
                      backgroundColor: 'rgba(248, 250, 252, 0.9)',
                      border: '1px solid rgba(224, 227, 231, 0.9)',
                      borderRadius: 1
                    }}
                  >
                    <Typography variant="subtitle1" gutterBottom fontWeight={500}>
                      Result Date:
                    </Typography>
                    
                    <Typography variant="h6" color="primary">
                      {resultDate 
                        ? format(resultDate, 'EEEE, MMMM d, yyyy') 
                        : 'Enter values and click Calculate'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Box>
        </Paper>
      </Box>
    </PageBackground>
  );
};

export default EasyAccess; 